import type { ReactNode, ButtonHTMLAttributes } from "react";
import { motion } from "motion/react";
import { Icon, type IconName } from "./Icon";
import { haptic } from "../telegram/haptics";
import { micro, pressButton, pressSpring, slow } from "../motion";
import s from "./ui.module.css";

/* ---------------------------------------------------------------- button -- */

type Variant = "primary" | "secondary" | "ghost" | "destructive";

/**
 * What a button feels like under the finger.
 *
 * A light impact is right for almost everything, which is why it is the
 * default and why almost nothing passes this. The exceptions are real: moving
 * between two views is a selection tick, and confirming a delete should not
 * feel identical to cancelling it.
 */
type Feedback = "tap" | "select" | "commit" | "none";

function feel(kind: Feedback): void {
  if (kind === "tap") haptic.tap();
  else if (kind === "select") haptic.select();
  else if (kind === "commit") haptic.commit();
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  icon?: IconName;
  feedback?: Feedback;
  children: ReactNode;
}

export function Button({
  variant = "secondary",
  icon,
  feedback = "tap",
  children,
  onClick,
  className,
  ...rest
}: ButtonProps) {
  return (
    <motion.button
      type="button"
      className={[s.button, s[variant], variant === "secondary" && "paper", className]
        .filter(Boolean)
        .join(" ")}
      onClick={(event) => {
        feel(feedback);
        onClick?.(event);
      }}
      {...pressButton}
      {...(rest as object)}
    >
      {icon && <Icon name={icon} size={20} />}
      {children}
    </motion.button>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  name: IconName;
  label: string;
  size?: 20 | 24;
  feedback?: Feedback;
}

export function IconButton({
  name,
  label,
  size = 20,
  feedback = "tap",
  onClick,
  className,
  ...rest
}: IconButtonProps) {
  return (
    <motion.button
      type="button"
      className={[s.iconButton, className].filter(Boolean).join(" ")}
      aria-label={label}
      onClick={(event) => {
        feel(feedback);
        onClick?.(event);
      }}
      {...pressButton}
      {...(rest as object)}
    >
      <Icon name={name} size={size} />
    </motion.button>
  );
}

/* ------------------------------------------------------------------ chip -- */

interface ChipProps {
  children: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  /** Shared across a group so the highlight slides between chips. */
  layoutGroup?: string;
  count?: number;
  icon?: IconName;
}

export function Chip({ children, selected, onClick, layoutGroup, count, icon }: ChipProps) {
  const interactive = Boolean(onClick);
  return (
    <motion.button
      type="button"
      disabled={!interactive}
      className={[
        s.chip,
        selected ? s.chipSelected : "paper",
        // Without a group there is no travelling pill to paint the highlight,
        // so the chip paints it itself. Same tint either way.
        selected && !layoutGroup && s.chipSelectedFlat,
        !interactive && s.chipStatic,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-pressed={interactive ? Boolean(selected) : undefined}
      onClick={() => {
        haptic.select();
        onClick?.();
      }}
      whileTap={interactive ? { scale: 0.96 } : undefined}
      transition={pressSpring}
    >
      {selected && layoutGroup && (
        <motion.span
          layoutId={`chip-${layoutGroup}`}
          className={s.chipPill}
          transition={pressSpring}
        />
      )}
      {icon && <Icon name={icon} size={16} />}
      {children}
      {count !== undefined && <span className="mono">{count}</span>}
    </motion.button>
  );
}

export function ChipRow({ children }: { children: ReactNode }) {
  return <div className={s.chipRow}>{children}</div>;
}

export function Badge({ value }: { value: number }) {
  return <span className={s.badge}>{value}</span>;
}

/* ------------------------------------------------------- loading + states -- */

export function Skeleton({
  width,
  height,
  radius,
}: {
  width?: string | number;
  height?: string | number;
  radius?: string;
}) {
  return <div className={s.skeleton} style={{ width, height, borderRadius: radius }} aria-hidden />;
}

export function Spinner({ size = 20 }: { size?: 16 | 20 | 24 }) {
  return (
    <span className={s.spinner}>
      <Icon name="spinner" size={size} />
    </span>
  );
}

interface StateProps {
  icon: IconName;
  title: string;
  body?: string;
  action?: ReactNode;
}

const stateItem = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: slow },
};

export function StateMessage({ icon, title, body, action }: StateProps) {
  return (
    <motion.div
      className={s.state}
      initial="hidden"
      animate="show"
      variants={{ show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } }}
    >
      <motion.div
        className={`${s.stateMark} paper`}
        variants={{
          hidden: { opacity: 0, scale: 0.86 },
          show: { opacity: 1, scale: 1, transition: slow },
        }}
      >
        <Icon name={icon} size={32} />
      </motion.div>
      <motion.p className={s.stateTitle} variants={stateItem}>
        {title}
      </motion.p>
      {body && (
        <motion.p className={s.stateBody} variants={stateItem}>
          {body}
        </motion.p>
      )}
      {action && (
        <motion.div className={s.stateAction} variants={stateItem}>
          {action}
        </motion.div>
      )}
    </motion.div>
  );
}

/* -------------------------------------------------------------- progress -- */

export function ProgressBar({ value }: { value: number }) {
  return (
    <div
      className={s.progress}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
    >
      {/* scaleX, not width: an animated width relayouts the bar on every frame
          of an upload. The physical origin a scale needs lives in the
          stylesheet, pinned per direction, so it still fills leading-edge
          first in both. Never quite zero — an empty bar still shows a nub. */}
      <motion.div
        className={s.progressFill}
        initial={false}
        animate={{ scaleX: Math.max(0.02, Math.min(1, value)) }}
        transition={micro}
      />
    </div>
  );
}

export function Divider() {
  return <hr className={s.divider} />;
}
